/** Member calls on routes that exist on `main` (workers/api/src/members, auth/routes). Shapes copied from the route files. */
import type { ApiClient } from "./client";

export interface PlanSummary {
  id: string;
  itemId: string | null;
  name: string | null;
  startDate: string;
  endDate: string;
  /** Computed on read from the plan's branch-local today (R4); never derived in the app. */
  status: "active" | "frozen" | "upcoming" | "expired";
}

export interface Member {
  id: string;
  name: string;
  /** Masked (`******1234`) unless the role has `see_phone`. */
  phone: string;
  email: string | null;
  homeBranchId: string | null;
  ownerStaffId: string | null;
  status: "active" | "inactive";
  createdAt: number;
  updatedAt: number;
}
export interface MemberRow extends Member { currentPlan: PlanSummary | null }

/** `balanceDue` and `walletBalance` are null without `revenue.member_balance` (server redaction, D57). */
export interface MemberProfile { member: Member; balanceDue: number | null; walletBalance: number | null }

export type ListStatus = "active" | "frozen" | "expired";
export interface ListQuery { q?: string; status?: ListStatus; expiringBefore?: string; limit?: number; cursor?: string | null }
export interface MemberPage { members: MemberRow[]; nextCursor: string | null }

export const PAGE_SIZE = 50;

export const listMembers = (c: ApiClient, q: ListQuery, signal?: AbortSignal) =>
  c.get<MemberPage>("/members", { q: q.q?.trim(), status: q.status, expiringBefore: q.expiringBefore, limit: q.limit ?? PAGE_SIZE, cursor: q.cursor ?? undefined }, signal);

export const getMember = (c: ApiClient, id: string) => c.get<MemberProfile & { plans: unknown[] }>(`/members/${id}`).then(({ member, balanceDue, walletBalance }) => ({ member, balanceDue, walletBalance }));

export interface NewMember { name: string; phone: string; email?: string; homeBranchId: string; ownerStaffId?: string }
export interface CreatedMember { member: Member; created: boolean }
export const createMember = (c: ApiClient, body: NewMember, idempotencyKey?: string) => c.post<CreatedMember>("/members", body, { idempotencyKey });

export type MemberPatch = Partial<{ name: string; email: string | null; homeBranchId: string; ownerStaffId: string }>;
export const patchMember = (c: ApiClient, id: string, body: MemberPatch) => c.patch<{ member: Member }>(`/members/${id}`, body);

/** A member's bill as `GET /bills` lists it: money figures are null without `revenue.bill_amounts`. */
export interface Bill {
  id: string; memberId: string; branchId: string; currency: string; status: string; total: number | null; paidTotal: number | null; balance: number | null;
  paymentStatus: "unpaid" | "partial" | "paid" | "overpaid"; businessDate: string; dueDate: string | null; createdAt: number; voidedAt: number | null;
}
export const listMemberBills = (c: ApiClient, memberId: string, cursor?: string | null) =>
  c.get<{ bills: Bill[]; nextCursor: string | null }>("/bills", { memberId, limit: PAGE_SIZE, cursor: cursor ?? undefined });

/** `POST /auth/frontdesk-code`: the code is shown once, to the person at the desk. Needs the member's real number. */
export interface FrontDeskCode { challengeId: string; code: string; expiresAt: number }
export const issueFrontDeskCode = (c: ApiClient, phone: string) => c.post<FrontDeskCode>("/auth/frontdesk-code", { phone });
